import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth config helper: send the httpOnly `admin_token` cookie + (legacy) Bearer header
// on admin API calls only. Public calls use plain axios so they don't trigger a
// credentialed CORS preflight (browsers reject credentials with `Access-Control-
// Allow-Origin: *`, which would cause every public fetch to fail under wildcard CORS).
const getAuthHeader = () => {
  const legacyToken = localStorage.getItem('admin_token');
  return legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {};
};

// Use this as the THIRD arg (config) of any axios call to a protected admin endpoint.
// Returns `{ headers: { Authorization?: '...' }, withCredentials: true }`.
const getAuthConfig = () => ({
  headers: getAuthHeader(),
  withCredentials: true,
});

// Helper to convert relative URL to full URL
const toFullUrl = (url) => {
  if (url && url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Auth — logout server-side clears the httpOnly cookie.
export const logoutAdmin = async () => {
  await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
};

// Image Upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await axios.post(`${API}/upload/image`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    });
    // Convert relative URL to full URL
    const data = response.data;
    if (data.url) {
      data.url = toFullUrl(data.url);
    }
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Upload failed');
  }
};

export const uploadMultipleImages = async (files) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  
  try {
    const response = await axios.post(`${API}/upload/images`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    });
    // Convert relative URLs to full URLs
    const data = response.data;
    if (data.images && Array.isArray(data.images)) {
      data.images = data.images.map(img => ({
        ...img,
        url: toFullUrl(img.url)
      }));
    }
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Upload failed');
  }
};

export const getUploadedImages = async () => {
  const response = await axios.get(`${API}/upload/images`, getAuthConfig());
  return response.data;
};

export const deleteUploadedImage = async (id) => {
  const response = await axios.delete(`${API}/upload/image/${id}`, getAuthConfig());
  return response.data;
};

// Heroes
export const getHeroes = async () => {
  const response = await axios.get(`${API}/heroes`);
  return response.data;
};

export const createHero = async (data) => {
  const response = await axios.post(`${API}/heroes`, data, getAuthConfig());
  return response.data;
};

export const updateHero = async (id, data) => {
  const response = await axios.put(`${API}/heroes/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteHero = async (id) => {
  const response = await axios.delete(`${API}/heroes/${id}`, getAuthConfig());
  return response.data;
};

// News
export const getNews = async (limit = 10) => {
  const response = await axios.get(`${API}/news?limit=${limit}`);
  return response.data;
};

export const createNews = async (data) => {
  const response = await axios.post(`${API}/news`, data, getAuthConfig());
  return response.data;
};

export const updateNews = async (id, data) => {
  const response = await axios.put(`${API}/news/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteNews = async (id) => {
  const response = await axios.delete(`${API}/news/${id}`, getAuthConfig());
  return response.data;
};

// Events
export const getEvents = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await axios.get(`${API}/events?${queryString}`);
  return response.data;
};

export const createEvent = async (data) => {
  const response = await axios.post(`${API}/events`, data, getAuthConfig());
  return response.data;
};

export const updateEvent = async (id, data) => {
  const response = await axios.put(`${API}/events/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteEvent = async (id) => {
  const response = await axios.delete(`${API}/events/${id}`, getAuthConfig());
  return response.data;
};

// Photos
export const getPhotos = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await axios.get(`${API}/photos?${queryString}`);
  return response.data;
};

export const createPhoto = async (data) => {
  const response = await axios.post(`${API}/photos`, data, getAuthConfig());
  return response.data;
};

export const updatePhoto = async (id, data) => {
  const response = await axios.put(`${API}/photos/${id}`, data, getAuthConfig());
  return response.data;
};

export const deletePhoto = async (id) => {
  const response = await axios.delete(`${API}/photos/${id}`, getAuthConfig());
  return response.data;
};

export const assignPhotoToAlbum = async (photoId, albumId) => {
  const response = await axios.put(
    `${API}/photos/${photoId}/album`,
    { album_id: albumId || null },
    getAuthConfig()
  );
  return response.data;
};

// Photo Albums
export const getAlbums = async (includeUnpublished = false) => {
  const url = includeUnpublished ? `${API}/albums?include_unpublished=true` : `${API}/albums`;
  const response = await axios.get(url);
  return response.data;
};

export const getAlbum = async (id) => {
  const response = await axios.get(`${API}/albums/${id}`);
  return response.data;
};

export const getAlbumPhotos = async (id) => {
  const response = await axios.get(`${API}/albums/${id}/photos`);
  return response.data;
};

export const createAlbum = async (data) => {
  const response = await axios.post(`${API}/admin/albums`, data, getAuthConfig());
  return response.data;
};

export const updateAlbum = async (id, data) => {
  const response = await axios.put(`${API}/admin/albums/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteAlbum = async (id) => {
  const response = await axios.delete(`${API}/admin/albums/${id}`, getAuthConfig());
  return response.data;
};

// Prayers
export const getPrayers = async (prayerType = null) => {
  const url = prayerType ? `${API}/prayers?prayer_type=${prayerType}` : `${API}/prayers`;
  const response = await axios.get(url);
  return response.data;
};

export const createPrayer = async (data) => {
  const response = await axios.post(`${API}/prayers`, data, getAuthConfig());
  return response.data;
};

export const updatePrayer = async (id, data) => {
  const response = await axios.put(`${API}/prayers/${id}`, data, getAuthConfig());
  return response.data;
};

export const deletePrayer = async (id) => {
  const response = await axios.delete(`${API}/prayers/${id}`, getAuthConfig());
  return response.data;
};

// Bible Readings
export const getBibleReadings = async (date = null) => {
  const url = date ? `${API}/bible-readings?date=${date}` : `${API}/bible-readings`;
  const response = await axios.get(url);
  return response.data;
};

export const getTodayBibleReading = async () => {
  const response = await axios.get(`${API}/bible-readings/today`);
  return response.data;
};

export const createBibleReading = async (data) => {
  const response = await axios.post(`${API}/bible-readings`, data, getAuthConfig());
  return response.data;
};

export const updateBibleReading = async (id, data) => {
  const response = await axios.put(`${API}/bible-readings/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteBibleReading = async (id) => {
  const response = await axios.delete(`${API}/bible-readings/${id}`, getAuthConfig());
  return response.data;
};

export const fetchWeekReadingsFromKatolskakyrkan = async (week, year) => {
  const response = await axios.post(
    `${API}/admin/fetch-week-readings`,
    null,
    {
      params: { week, year },
      headers: getAuthHeader(),
      withCredentials: true,
    }
  );
  return response.data;
};

// Bible Readings Settings
export const getBibleReadingsSettings = async () => {
  const response = await axios.get(`${API}/bible-readings-settings`);
  return response.data;
};

export const updateBibleReadingsSettings = async (autoFetchEnabled, fetchIntervalHours, weeksAhead) => {
  const response = await axios.put(`${API}/bible-readings-settings`, {
    auto_fetch_enabled: autoFetchEnabled,
    fetch_interval_hours: fetchIntervalHours,
    weeks_ahead: weeksAhead
  }, getAuthConfig());
  return response.data;
};

export const fetchBibleReadingsNow = async (weeksAhead = 3) => {
  const response = await axios.post(`${API}/admin/fetch-readings-now`, {
    weeks_ahead: weeksAhead
  }, getAuthConfig());
  return response.data;
};

// Mass Schedules
export const getMassSchedules = async (church = null) => {
  const url = church ? `${API}/mass-schedules?church=${church}` : `${API}/mass-schedules`;
  const response = await axios.get(url);
  return response.data;
};

export const createMassSchedule = async (data) => {
  const response = await axios.post(`${API}/mass-schedules`, data, getAuthConfig());
  return response.data;
};

export const updateMassSchedule = async (id, data) => {
  const response = await axios.put(`${API}/mass-schedules/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteMassSchedule = async (id) => {
  const response = await axios.delete(`${API}/mass-schedules/${id}`, getAuthConfig());
  return response.data;
};

// Language Settings
export const getLanguageSettings = async () => {
  const response = await axios.get(`${API}/language-settings`);
  return response.data;
};

export const updateLanguageSettings = async (data) => {
  const response = await axios.put(`${API}/language-settings`, data, getAuthConfig());
  return response.data;
};

// Admin Notification Settings
export const getAdminNotificationEmail = async () => {
  const response = await axios.get(`${API}/settings/admin-notification`);
  return response.data;
};

export const updateAdminNotificationEmail = async (email) => {
  const response = await axios.put(`${API}/settings/admin-notification`, { admin_notification_email: email }, getAuthConfig());
  return response.data;
};

// Cached Translations
export const getCachedTranslations = async (contentType, contentId, lang = null) => {
  const url = lang 
    ? `${API}/translations/${contentType}/${contentId}?lang=${lang}`
    : `${API}/translations/${contentType}/${contentId}`;
  const response = await axios.get(url);
  return response.data;
};

// Auto updates
export const updateRSSFeeds = async () => {
  const response = await axios.post(`${API}/admin/update-rss-feeds`, {}, getAuthConfig());
  return response.data;
};

export const updateDailyReadings = async () => {
  const response = await axios.post(`${API}/admin/update-daily-readings`, {}, getAuthConfig());
  return response.data;
};

// News by Category
export const getNewsByCategory = async (limit = 20) => {
  const response = await axios.get(`${API}/news/by-category?limit=${limit}`);
  return response.data;
};

// RSS Settings
export const getRssSettings = async () => {
  const response = await axios.get(`${API}/rss-settings`);
  return response.data;
};

export const updateRssSettings = async (autoFetchEnabled, fetchIntervalHours) => {
  const response = await axios.put(`${API}/rss-settings`, {
    auto_fetch_enabled: autoFetchEnabled,
    fetch_interval_hours: fetchIntervalHours
  }, getAuthConfig());
  return response.data;
};

// Calendar Events
export const getCalendarSettings = async () => {
  const response = await axios.get(`${API}/calendar-settings`);
  return response.data;
};

export const updateCalendarSettings = async (settings) => {
  const response = await axios.put(`${API}/calendar-settings`, settings, getAuthConfig());
  return response.data;
};

export const fetchCalendars = async () => {
  // Use a longer timeout as CalDAV fetch can take time
  const response = await axios.post(`${API}/admin/fetch-calendars`, {}, {
    headers: getAuthHeader(),
    withCredentials: true,
    timeout: 120000 // 2 minutes timeout
  });
  return response.data;
};

export const fetchSingleCalendar = async (churchId, clearFirst = false, type = null) => {
  // Fetch calendar for a single church. Pass type='mass' or 'event' to scope the sync.
  const qs = new URLSearchParams({ clear_first: String(clearFirst) });
  if (type) qs.set('type', type);
  const response = await axios.post(
    `${API}/admin/fetch-calendar/${churchId}?${qs.toString()}`,
    {},
    {
      headers: getAuthHeader(),
      withCredentials: true,
      timeout: 180000 // 3 minutes timeout for single church
    }
  );
  return response.data;
};

export const clearChurchCalendar = async (churchId) => {
  const response = await axios.delete(`${API}/admin/calendar-events/church/${churchId}`, getAuthConfig());
  return response.data;
};

export const clearAllCalendars = async () => {
  const response = await axios.delete(`${API}/admin/calendar-events/stale`, getAuthConfig());
  return response.data;
};

export const getCalendarEvents = async (church = null, weekOffset = 0) => {
  const params = new URLSearchParams();
  if (church) params.append('church', church);
  params.append('week_offset', weekOffset.toString());
  const response = await axios.get(`${API}/calendar-events?${params.toString()}`);
  return response.data;
};

export const getAllCalendarEvents = async () => {
  const response = await axios.get(`${API}/calendar-events/all`);
  return response.data;
};

// Manual Liturgy Events
export const getManualLiturgyEvents = async () => {
  const response = await axios.get(`${API}/manual-liturgy-events`);
  return response.data;
};

export const createManualLiturgyEvent = async (data) => {
  const response = await axios.post(`${API}/admin/manual-liturgy-events`, data, getAuthConfig());
  return response.data;
};

export const updateManualLiturgyEvent = async (id, data) => {
  const response = await axios.put(`${API}/admin/manual-liturgy-events/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteManualLiturgyEvent = async (id) => {
  const response = await axios.delete(`${API}/admin/manual-liturgy-events/${id}`, getAuthConfig());
  return response.data;
};

// Calendar Sync Helpers
export const syncCalendarForChurch = async (churchId) => {
  const response = await axios.post(
    `${API}/admin/fetch-calendar/${churchId}`,
    {},
    { headers: getAuthHeader(), withCredentials: true, timeout: 120000 }
  );
  return response.data;
};

export const syncAllCalendars = async () => {
  const response = await axios.post(
    `${API}/admin/fetch-calendars`,
    {},
    { headers: getAuthHeader(), withCredentials: true, timeout: 180000 }
  );
  return response.data;
};

// Admin Calendar Events CRUD (for Liturgi Manager)
export const getAdminCalendarEvents = async () => {
  const response = await axios.get(`${API}/admin/calendar-events`, getAuthConfig());
  return response.data;
};

export const createAdminCalendarEvent = async (data) => {
  const response = await axios.post(`${API}/admin/calendar-events`, data, getAuthConfig());
  return response.data;
};

export const updateAdminCalendarEvent = async (uid, data) => {
  const response = await axios.put(`${API}/admin/calendar-events/${uid}`, data, getAuthConfig());
  return response.data;
};

export const deleteAdminCalendarEvent = async (uid) => {
  const response = await axios.delete(`${API}/admin/calendar-events/${uid}`, getAuthConfig());
  return response.data;
};

// Featured Announcements
export const getFeaturedAnnouncements = async (includeHidden = false) => {
  const response = await axios.get(`${API}/featured-announcements`, { params: { include_hidden: includeHidden } });
  return response.data;
};

export const getAdminFeaturedAnnouncements = async () => {
  const response = await axios.get(`${API}/admin/featured-announcements`, getAuthConfig());
  return response.data;
};

export const createFeaturedAnnouncement = async (data) => {
  const response = await axios.post(`${API}/admin/featured-announcements`, data, getAuthConfig());
  return response.data;
};

export const updateFeaturedAnnouncement = async (id, data) => {
  const response = await axios.put(`${API}/admin/featured-announcements/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteFeaturedAnnouncement = async (id) => {
  const response = await axios.delete(`${API}/admin/featured-announcements/${id}`, getAuthConfig());
  return response.data;
};

export const getFeaturedAnnouncementsSettings = async () => {
  const response = await axios.get(`${API}/featured-announcements/settings`);
  return response.data;
};

export const updateFeaturedAnnouncementsSettings = async (data) => {
  const response = await axios.put(`${API}/admin/featured-announcements/settings`, data, getAuthConfig());
  return response.data;
};

// Translation
export const translateText = async (text, targetLang, sourceLang = 'sv') => {
  const response = await axios.post(`${API}/translate`, {
    text,
    target_lang: targetLang,
    source_lang: sourceLang
  });
  return response.data;
};

// Glossary
export const getGlossary = async () => {
  const response = await axios.get(`${API}/glossary`);
  return response.data;
};

export const updateGlossary = async (glossary) => {
  const response = await axios.put(`${API}/glossary`, glossary, getAuthConfig());
  return response.data;
};

// Users Management
export const getUsers = async () => {
  const response = await axios.get(`${API}/admin/users`, getAuthConfig());
  return response.data;
};

export const createUser = async (userData) => {
  const response = await axios.post(`${API}/admin/users`, userData, getAuthConfig());
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await axios.put(`${API}/admin/users/${userId}`, userData, getAuthConfig());
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await axios.delete(`${API}/admin/users/${userId}`, getAuthConfig());
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await axios.get(`${API}/admin/me`, getAuthConfig());
  return response.data;
};

// Page Banners
export const getPageBanners = async () => {
  const response = await axios.get(`${API}/page-banners`);
  return response.data;
};

export const getPageBanner = async (pageId) => {
  const response = await axios.get(`${API}/page-banners/${pageId}`);
  return response.data;
};

export const createPageBanner = async (data) => {
  const response = await axios.post(`${API}/page-banners`, data, getAuthConfig());
  return response.data;
};

export const updatePageBanner = async (pageId, data) => {
  const response = await axios.put(`${API}/page-banners/${pageId}`, data, getAuthConfig());
  return response.data;
};

export const deletePageBanner = async (pageId) => {
  const response = await axios.delete(`${API}/page-banners/${pageId}`, getAuthConfig());
  return response.data;
};
// Contact Form
export const sendContactMessage = async (data) => {
  const response = await axios.post(`${API}/contact`, data);
  return response.data;
};

// Announcements
export const getAnnouncements = async (church = null, visibleOnly = true) => {
  const params = new URLSearchParams();
  if (church) params.append('church', church);
  params.append('visible_only', visibleOnly.toString());
  const response = await axios.get(`${API}/announcements?${params.toString()}`);
  return response.data;
};

export const getAnnouncement = async (id) => {
  const response = await axios.get(`${API}/announcements/${id}`);
  return response.data;
};

export const createAnnouncement = async (data) => {
  const response = await axios.post(`${API}/announcements`, data, getAuthConfig());
  return response.data;
};

export const updateAnnouncement = async (id, data) => {
  const response = await axios.put(`${API}/announcements/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteAnnouncement = async (id) => {
  const response = await axios.delete(`${API}/announcements/${id}`, getAuthConfig());
  return response.data;
};

// Ticker Settings
export const getTickerSettings = async () => {
  const response = await axios.get(`${API}/settings/ticker`);
  return response.data;
};

export const updateTickerSettings = async (tickerMaxChars) => {
  const response = await axios.put(`${API}/settings/ticker`, { ticker_max_chars: tickerMaxChars }, getAuthConfig());
  return response.data;
};

// Articles (Fördjupning)
export const getArticles = async (category = null) => {
  const params = category ? `?category=${category}` : '';
  const response = await axios.get(`${API}/articles${params}`);
  return response.data;
};

export const getArticle = async (id) => {
  const response = await axios.get(`${API}/articles/${id}`);
  return response.data;
};

export const createArticle = async (data) => {
  const response = await axios.post(`${API}/articles`, data, getAuthConfig());
  return response.data;
};

export const updateArticle = async (id, data) => {
  const response = await axios.put(`${API}/articles/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteArticle = async (id) => {
  const response = await axios.delete(`${API}/articles/${id}`, getAuthConfig());
  return response.data;
};

// Parish Pages (Trosundervisning, Ungdomar)
export const getParishPage = async (pageType) => {
  const response = await axios.get(`${API}/parish-pages/${pageType}`);
  return response.data;
};

export const updateParishPage = async (pageType, data) => {
  const response = await axios.put(`${API}/parish-pages/${pageType}`, data, getAuthConfig());
  return response.data;
};

// Catalogue Items
export const getCatalogueItems = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.category) params.append('category', options.category);
  if (options.featured_only) params.append('featured_only', 'true');
  if (options.active_only !== undefined) params.append('active_only', options.active_only);
  
  const response = await axios.get(`${API}/catalogue?${params.toString()}`);
  return response.data;
};

export const getCatalogueItem = async (id) => {
  const response = await axios.get(`${API}/catalogue/${id}`);
  return response.data;
};

export const createCatalogueItem = async (data) => {
  const response = await axios.post(`${API}/admin/catalogue`, data, getAuthConfig());
  return response.data;
};

export const updateCatalogueItem = async (id, data) => {
  const response = await axios.put(`${API}/admin/catalogue/${id}`, data, getAuthConfig());
  return response.data;
};

export const deleteCatalogueItem = async (id) => {
  const response = await axios.delete(`${API}/admin/catalogue/${id}`, getAuthConfig());
  return response.data;
};


// --- Theme Manager ---
export const getThemes = async () => {
  const response = await axios.get(`${API}/themes`);
  return response.data;
};

export const getActiveTheme = async () => {
  const response = await axios.get(`${API}/theme/active`);
  return response.data;
};

export const setActiveTheme = async (themeId) => {
  const response = await axios.put(
    `${API}/admin/theme/active`,
    { theme_id: themeId },
    getAuthConfig()
  );
  return response.data;
};

export const saveCustomTheme = async ({ name, description, colors }) => {
  const response = await axios.put(
    `${API}/admin/theme/custom`,
    { name, description, colors },
    getAuthConfig()
  );
  return response.data;
};
